import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { CategoriesMobileDisplayOption } from 'types/graphql';

type ScrollListProps = {
  children: ReactNode;
  mobileDisplay: CategoriesMobileDisplayOption;
  gap?: number;
  itemCount?: number;
  showViewAll?: boolean;
  showHeading?: boolean;
  isExpanded?: boolean;
  viewAllItems?: () => void;
};

export const CategoriesList = ({
  children,
  mobileDisplay,
  gap = 12,
  itemCount = 0,
  showViewAll = true,
  showHeading = true,
  isExpanded,
  viewAllItems,
}: ScrollListProps): JSX.Element => (
  <Wrapper data-testid='categories-list'>
    {showHeading && <Heading>Categories</Heading>}

    <Scroller>
      <List $mobileDisplay={mobileDisplay} $gap={gap}>
        {children}
      </List>
    </Scroller>

    {showViewAll &&
      itemCount > 6 &&
      mobileDisplay === CategoriesMobileDisplayOption.grid &&
      !isExpanded &&
      viewAllItems && <ViewAll onClick={viewAllItems}>View All</ViewAll>}
  </Wrapper>
);

const Wrapper = styled.div`
  margin: 20px 0 0;
`;

const Heading = styled.h2`
  color: #2c3236;
  font-size: 20px;
  font-weight: 700;
  line-height: ${24 / 20};
  margin: 0 20px 24px;
`;

const Scroller = styled.div`
  overflow-x: auto;
  padding: 0 20px 20px;
  margin-bottom: -20px;

  scroll-behavior: smooth;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const List = styled.ul<{
  $mobileDisplay: CategoriesMobileDisplayOption;
  $gap: number;
}>`
  display: flex;
  gap: ${({ $gap }) => $gap}px;
  list-style: none;

  ${({ $mobileDisplay }) =>
    $mobileDisplay === CategoriesMobileDisplayOption.row &&
    css`
      &::after {
        content: '';
        flex: 0 0 1px;
      }
    `}

  ${({ $mobileDisplay }) =>
    $mobileDisplay === CategoriesMobileDisplayOption.grid &&
    css`
      flex-wrap: wrap;
    `}
`;

const ViewAll = styled.button`
  appearance: none;
  background: #e3e7e9;
  border: none;
  border-radius: ${({ theme }) => theme.radius.buttons};
  color: #2c3236;
  cursor: pointer;
  display: block;
  font-size: 16px;
  font-weight: 600;
  margin: 24px 20px 0;
  line-height: ${24 / 16};
  padding: 16px;
  text-align: center;
  width: calc(100% - 40px);
`;
